import React, { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { usePropertyField } from '@vibesites/runtime/react';
import {
  Header as BaseHeader,
  contentSchema as baseSchema,
} from '@vibesites-weidner-apartment-homes/theme-via6/components/_HeaderBase';
import basePagesData from '@vibesites/theme-via6/pages.json';
import childPagesData from '@vibesites-weidner-apartment-homes/theme-via6/pages.json';

// Mirror the parent+child shallow-merge in `_HeaderBase.tsx`. `Header.tsx`
// uses `pagesData` for the mobile-drawer child-row marker — it needs to see
// Weidner's `parent: <alias>` declarations the same way the base does so
// labels of nested pages (Amenities, Pet Policy, Future Residents Club,
// Virtual Tour) get tagged for indentation in the drawer.
// Type is intentionally `any` so the rest of the file (which `as`-casts this
// to specific shapes inside `buildChildLabelSet`) continues to work unchanged.
const pagesData: any = (() => {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [alias, data] of Object.entries(basePagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...data };
  }
  for (const [alias, data] of Object.entries(childPagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...out[alias], ...data };
  }
  return out;
})();

/**
 * The Majestic Header.
 *
 * Forked from `@vibesites-weidner-apartment-homes/theme-via6/components/Header`
 * — identical Via6 brand styling, except the logo-image pin is swapped from
 * `property_primary_logo` (Weidner default) to `property_mono_white_logo`,
 * per the brand owner's request. The Weidner parent already overrides the
 * base rise theme's monochrome scroll-swap with a MutationObserver, so a
 * pure style wrapper / slot-default tweak in this property repo can't
 * survive — the parent observer would re-pin the primary logo. Forking is
 * the only path that lets us change which property field the observer
 * pins. See the "Force the mono-white logo in every header state" comment
 * block below for details.
 *
 * Repaints the rise theme's transparent-over-hero / white-on-scroll header
 * as a Via6-style solid theme-color bar. The base Header doesn't accept a
 * className, so we wrap it and ship a <style> block that forces:
 *
 *   - The fixed <header> + .header-bar background to --vibe-color-primary
 *     in every state (overrides both the bg-transparent default and the
 *     inline `backgroundColor: var(--vibe-color-background)` the base
 *     applies on scroll — inline styles without !important lose to !important CSS).
 *   - All nav / utility-row link text to --vibe-color-text-on-primary so it
 *     stays readable against the new solid surface.
 *   - The Schedule-a-Tour CTA flips to a white outline (border + label use
 *     text-on-primary) instead of the primary-on-primary it would otherwise
 *     paint, with a white-fill / primary-label hover.
 *   - The utility-row divider line softens to a translucent on-primary tint
 *     so it still reads against the new bg without the hard --vibe-color-border line.
 *
 * The base Header's schema travels with us via the re-attach below — without
 * that line the editor would see no editing affordances on the wrapper.
 */
// ============================================================================
// Rename "Resident Access" → "Residents", swap its position with Apply Now,
// and source its URL from the per-property resident-portal field.
//
// Why this can't be done in pages.json / the slot definition:
//   The base rise Header owns the `header.resident_access_link` slot group
//   definition — including its label default ("Resident Access"), URL
//   default ("/resident-access"), and the hardcoded render order in the
//   utility row (Apply Now first, Resident Access second). Slot field
//   defaults live in the SlotGroupDefinition passed to useSlotGroup, which
//   isn't exposed for override from a wrapper, and the row order is JSX
//   structure — not data-driven. Via6's brand wants:
//
//     - The link relabelled to "Residents" (sentence-case, shorter).
//     - Its URL sourced from property_resident_portal_url so each property
//       points at its own portal rather than the static /resident-access
//       interior page (which exists as a fallback but is hidden from nav
//       and used only when the portal field is empty).
//     - The Residents pill positioned BEFORE Apply Now, so the right-
//       aligned utility group reads Residents | Apply Now and the mobile
//       drawer pill grid reads [Residents] [Apply Now].
//
// Identification strategy:
//   EditableSlotGroup renders a plain <div> with no className / data-attr
//   in preview/production (the data-vibesite-slot-group attribute is only
//   attached in edit mode). The only stable contract on the rendered
//   wrapper is the descendant anchor's text content — so we identify
//   wrappers by scanning the parent's children for one containing
//   `a.header-util-link` / `a.mobile-util-pill` whose trimmed textContent
//   matches a known label ("Apply Now" / "Resident Access" / "Residents").
//   This intentionally leaves editor-customized labels alone — if the user
//   has set a non-default slot label in the editor, we won't touch that
//   slot (the swap / rename / URL-override are all default-only).
//
// Idempotency:
//   - Rename: only fires when textContent === "Resident Access" (default).
//   - URL override: only fires when href === "/resident-access" (default).
//   - Reorder: skipped when Residents already precedes Apply Now in the
//     parent's child order.
//   The MutationObserver re-runs this on every nav reconcile (drawer
//   open/close, mobile breakpoint flip, React re-renders the slot groups
//   back into their original order), so it self-heals without churn.
// ============================================================================

const RESIDENT_DEFAULT_LABEL = 'Resident Access';
const RESIDENTS_NEW_LABEL = 'Residents';
const APPLY_DEFAULT_LABEL = 'Apply Now';
const RESIDENT_DEFAULT_URL = '/resident-access';

/**
 * Find the slot-wrapper element inside `parent` whose anchor descendant
 * (matching `anchorSelector`) has trimmed textContent in `labels`. Returns
 * null when no child wrapper matches.
 *
 * `Element.querySelector` only inspects descendants, not the element itself,
 * so direct child anchors are excluded — only the wrapper <div>s emitted by
 * EditableSlotGroup will surface a matching descendant.
 */
function findSlotWrapperByLabel(
  parent: Element,
  anchorSelector: string,
  labels: readonly string[],
): HTMLElement | null {
  for (const child of Array.from(parent.children)) {
    const anchor = child.querySelector?.(anchorSelector) as HTMLAnchorElement | null;
    if (!anchor) continue;
    const text = anchor.textContent?.trim();
    if (text && labels.includes(text)) return child as HTMLElement;
  }
  return null;
}

/**
 * Apply the Residents rename + URL override, then swap its wrapper with the
 * Apply Now wrapper inside the given `parent` container. The wrapper between
 * them (a `.header-util-divider` on desktop; nothing on mobile because the
 * pill grid lays out via `gap`) stays in place since we swap the two
 * wrappers by-reference rather than reinserting around it.
 */
function reorderResidentsAndApply(
  parent: HTMLElement,
  anchorSelector: string,
  residentPortalUrl: string | null | undefined,
) {
  const applyWrapper = findSlotWrapperByLabel(parent, anchorSelector, [APPLY_DEFAULT_LABEL]);
  const residentsWrapper = findSlotWrapperByLabel(parent, anchorSelector, [
    RESIDENT_DEFAULT_LABEL,
    RESIDENTS_NEW_LABEL,
  ]);
  if (!applyWrapper || !residentsWrapper) return;

  const residentsAnchor = residentsWrapper.querySelector<HTMLAnchorElement>(anchorSelector);
  if (residentsAnchor) {
    // Rename — only when the anchor still shows the base default label.
    if (residentsAnchor.textContent?.trim() === RESIDENT_DEFAULT_LABEL) {
      residentsAnchor.textContent = RESIDENTS_NEW_LABEL;
    }
    // URL override — only when the anchor still points at the default
    // fallback (`/resident-access`). If the editor has set a custom slot URL,
    // or the property field is empty, leave the existing href alone.
    if (
      residentPortalUrl &&
      residentsAnchor.getAttribute('href') === RESIDENT_DEFAULT_URL
    ) {
      residentsAnchor.setAttribute('href', residentPortalUrl);
    }
  }

  // Idempotency: if Residents already precedes Apply Now in the parent's
  // child order, the swap has already been applied this cycle.
  const pos = applyWrapper.compareDocumentPosition(residentsWrapper);
  if (pos & Node.DOCUMENT_POSITION_PRECEDING) return;

  // In-place position swap via a temporary placeholder so the divider
  // sandwiched between the two wrappers (desktop) stays put.
  //
  //   before: [..., apply,   divider, residents, ...]
  //   after:  [..., residents, divider, apply, ...]
  const parentNode = applyWrapper.parentNode;
  if (!parentNode) return;
  const placeholder = document.createElement('div');
  parentNode.replaceChild(placeholder, applyWrapper);
  parentNode.replaceChild(applyWrapper, residentsWrapper);
  parentNode.replaceChild(residentsWrapper, placeholder);
}

// ============================================================================
// Visually nest child pages under their parent in the mobile drawer.
//
// Why this can't be done in pages.json / via slot tweaks:
//   The base rise mobile drawer renders parents and their children as a flat
//   sibling list (`MobileNavLink` for the parent, then a `MobileNavLink` per
//   child) and explicitly DOESN'T apply the `isChild` offset — the base
//   author chose flush rendering because they felt the indentation read as
//   misaligned noise in a vertical list. Via6's brand wants the opposite:
//   the desktop nav already groups child pages inside a dropdown panel under
//   their parent (Gallery & Tours → Gallery / Schedule A Tour / Virtual Tour;
//   Features → Amenities / Pet Policy / Future Residents Club), and the
//   hamburger drawer should mirror that visual hierarchy so users see at a
//   glance which pages belong to which top-level section.
//
//   The base `MobileNavLink` doesn't accept a className override and the
//   `isChild` prop is intentionally unused, so we can't ask the existing
//   component to render children differently. Forking it would mean
//   replicating the whole drawer.
//
//   Surgical option: mark child anchors with a data attribute after the
//   drawer mounts, and let CSS in the <style> block paint the indent /
//   smaller-type treatment. Same DOM-injection pattern the home /
//   schedule-a-tour links above use.
//
// Identification strategy:
//   Children are identified by label match. We build a Set of child labels
//   from the merged base/pages.json object (the module-load merge above lands
//   Weidner's overrides on top of base, so this Set sees the final per-property
//   hierarchy). A page is a "child" when it has a string `parent` field
//   referencing an eligible (visible, non-template) parent.
//
// Idempotency:
//   The marker only sets `data-vibe-nav-child` when it isn't already there,
//   and the CSS treatment is class-attribute-driven (no React state). The
//   shared MutationObserver re-runs `markMobileNavChildren` on every drawer
//   reconcile (open/close, resize, react re-render of the slot groups), so
//   newly-rendered children pick up the marker without churn.
// ============================================================================

const NAV_CHILD_FLAG = 'data-vibe-nav-child';

function buildChildLabelSet(): Set<string> {
  const labels = new Set<string>();
  const pages = pagesData as Record<string, Record<string, unknown>>;

  // Eligibility mirrors the base buildNavLinks filter: skip hidden pages
  // (visible: false) and template-driven dynamic routes (hyperlocal). We
  // can't replicate the dynamic checks (virtual_tour_url present, apartment-
  // sync IDs present) here without re-running the runtime hooks — but those
  // hide INDIVIDUAL pages, not their parents, so the worst case is we mark
  // a label as "child" whose anchor never renders. Harmless.
  const eligible = new Set<string>();
  for (const [alias, page] of Object.entries(pages)) {
    if (page.visible === false) continue;
    if (page.template) continue;
    eligible.add(alias);
  }

  for (const [alias, page] of Object.entries(pages)) {
    if (!eligible.has(alias)) continue;
    const parent = page.parent;
    if (typeof parent !== 'string') continue;
    if (!eligible.has(parent) || parent === alias) continue;
    if (typeof page.name === 'string' && page.name) {
      labels.add(page.name);
    }
  }

  return labels;
}

// Built once at module load; pages.json contents don't change at runtime so
// the set is stable across observer re-runs.
const NAV_CHILD_LABELS = buildChildLabelSet();

function markMobileNavChildren() {
  if (typeof document === 'undefined') return;
  const mobileDrawerNav = document.querySelector('header.fixed .mobile-menu-panel nav');
  if (!mobileDrawerNav) return;
  // Only consider direct <a> children of the <nav> — the base drawer renders
  // every nav row (parent and child) as a flat sibling of <nav>, so anchors
  // nested deeper (e.g. inside the SVG / pill rows below) aren't candidates.
  for (const child of Array.from(mobileDrawerNav.children)) {
    if (!(child instanceof HTMLAnchorElement)) continue;
    if (child.hasAttribute(NAV_CHILD_FLAG)) continue;
    const label = child.textContent?.trim();
    if (label && NAV_CHILD_LABELS.has(label)) {
      child.setAttribute(NAV_CHILD_FLAG, '');
    }
  }
}

function injectResidentsSwap(residentPortalUrl: string | null | undefined) {
  if (typeof document === 'undefined') return;

  // Desktop — utility row's right-aligned flex group (innerRow.lastElementChild).
  const utilRow = document.querySelector('header.fixed .header-util-row');
  const innerRow = utilRow?.querySelector<HTMLElement>(
    '.flex.items-center.justify-between',
  );
  const rightGroup = innerRow?.lastElementChild as HTMLElement | null;
  if (rightGroup) {
    reorderResidentsAndApply(rightGroup, 'a.header-util-link', residentPortalUrl);
  }

  // Mobile drawer — 2-column pill grid.
  const mobileGrid = document.querySelector<HTMLElement>(
    'header.fixed .mobile-menu-panel .grid.grid-cols-2',
  );
  if (mobileGrid) {
    reorderResidentsAndApply(mobileGrid, 'a.mobile-util-pill', residentPortalUrl);
  }
}

export function Header() {
  // Pulled at the top level so it's available to the injection effect below
  // and re-runs the swap whenever the field's value flips (editor sets /
  // clears the URL → the Residents anchor's href is re-overridden on the
  // next reconcile). The field is the per-property Resident Portal URL — see
  // injectResidentsSwap below for how it's used as the default href.
  const residentPortalUrl = usePropertyField('property_resident_portal_url');

  useEffect(() => {
    const runInjections = () => {
      injectResidentsSwap(residentPortalUrl);
      markMobileNavChildren();
    };
    runInjections();
    // Re-inject whenever React reconciles the nav (resize → overflow
    // recalc strips our DOM node, mobile drawer remounts on open, etc.).
    // The Residents rename / swap AND the mobile drawer child-row marking
    // both share this one observer — each is presence-checked (data-* flags
    // or current-order / current-label checks) so re-runs are cheap no-ops.
    const observer = new MutationObserver(runInjections);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [residentPortalUrl]);

  // ==========================================================================
  // Lazy-load the "Prata" serif Google Font for the Via6 heading family.
  //
  // `tokens.ts` sets --vibe-font-heading to "'Prata', serif", and components
  // across the theme (Header, Hero pre_title/subtitle, DividerBG, HomeMap,
  // FloorplansList, etc.) render their headings against that CSS variable.
  // The runtime emits the token as a font-family string but does NOT fetch
  // the actual Prata web font — without this loader, the browser falls back
  // to the generic `serif` (Times/Georgia), which is what was happening
  // before. Via6Living.com loads Prata weight 400 from Google Fonts, so we
  // match that here.
  //
  // Header always mounts on every page, so this is the single spot that
  // guarantees Prata is available site-wide. Dedupe via the
  // `data-vibe-font="prata"` marker — same convention the Satisfy loaders
  // in FloorplansList / PropertyOverview / AmenitiesGrid / HomeMap already
  // use, so multiple injectors on the same page collapse to one <link>.
  // ==========================================================================
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="prata"]')) return;
    if (!document.querySelector('link[data-vibe-font-preconnect="googleapis"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      preconnect1.setAttribute('data-vibe-font-preconnect', 'googleapis');
      document.head.appendChild(preconnect1);
    }
    if (!document.querySelector('link[data-vibe-font-preconnect="gstatic"]')) {
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      preconnect2.setAttribute('data-vibe-font-preconnect', 'gstatic');
      document.head.appendChild(preconnect2);
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Prata&display=swap';
    link.setAttribute('data-vibe-font', 'prata');
    document.head.appendChild(link);
  }, []);

  // ==========================================================================
  // Force the mono-white logo in every header state. (The Majestic override.)
  //
  // The base rise Header swaps the logo image source between two property
  // fields based on scroll / hero state:
  //
  //     scrolled                 → mono_black_logo
  //     transparent over hero    → mono_white_logo
  //
  // …so the logo always contrasts against the bar's *current* background
  // (rise paints the bar transparent over the hero when unscrolled, and
  // solid --vibe-color-background / white when scrolled). Both code paths
  // only fall back to the editor-picked / property_primary_logo when the
  // mono_* property fields are empty.
  //
  // The Weidner parent Header overrides BOTH of those paths to force the
  // full-color `property_primary_logo` in every state, because the Via6
  // bar is repainted to a solid --vibe-color-primary surface where the
  // primary mark reads fine.
  //
  // For The Majestic specifically, the brand owner asked to swap that to
  // the mono-WHITE logo so the header reads as a single-color knockout
  // mark on the primary-color bar (and, importantly, on the secondary-
  // color utility row above it) rather than the full-color primary
  // variant the Weidner parent pins. Both states are the same dark
  // surface in this theme, so a single field pin is all we need.
  //
  // Both swaps are React-driven DOM changes (the `<img>` `src` + `srcset`
  // attrs re-render when scroll state flips), so CSS can't pin them — we
  // intercept the attribute changes and restore the mono-white logo URL.
  //
  // Approach:
  //   - Pull the mono-white logo URL from `property_mono_white_logo`.
  //     When that field is empty for a property, the effect short-circuits
  //     (no-op) and the rise / Weidner downstream behavior carries through
  //     — same fallback shape the parent uses for an empty primary logo.
  //   - Find the logo <img> (the only <img> under the `a[href="/"]
  //     .flex-shrink-0` logo anchor that rise renders), and whenever its
  //     src differs from the mono-white URL, force `src` back and clear
  //     `srcset` (the browser would otherwise prefer the srcset's
  //     primary / mono_black variants over our overridden `src`).
  //   - Re-run whenever the rise theme reconciles the header (scroll
  //     state flip → src/srcset swap; class flip on the outer header).
  //
  // The observer is scoped to the <header> element (not the whole body)
  // and filtered to the attributes that drive the swap (`src`, `srcset`
  // on the img; `class` on the header so we react to scroll-state flips),
  // so the mutation budget stays tight.
  // ==========================================================================
  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo');
  useEffect(() => {
    if (!monoWhiteLogoUrl) return;
    const header = document.querySelector<HTMLElement>('header.fixed');
    if (!header) return;

    const findLogoImg = () =>
      header.querySelector<HTMLImageElement>('a[href="/"].flex-shrink-0 img');

    function sync() {
      const img = findLogoImg();
      if (!img) return;
      if (img.getAttribute('src') !== monoWhiteLogoUrl) {
        img.setAttribute('src', monoWhiteLogoUrl!);
      }
      // The rise theme renders the logo via VibesitesImage, which emits a
      // `srcset` keyed off whatever URL it was passed (primary / mono_black
      // variant) — once we override `src`, the browser would still pick
      // from `srcset` and keep that variant. Clearing it forces the
      // browser to use our overridden `src` directly.
      if (img.hasAttribute('srcset')) {
        img.removeAttribute('srcset');
      }
    }

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(header, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'class'],
    });
    return () => observer.disconnect();
  }, [monoWhiteLogoUrl]);

  return (
    <>
      <BaseHeader />
      <style>{`
        /* ============================================================
           Solid theme-color header (Via6-style)
           Overrides the rise theme's transparent/scrolled background
           swap so the bar paints --vibe-color-primary in every state.
           ============================================================ */

        /* Outer fixed header element — kills the bg-transparent default
           and the inline backgroundColor the base applies on scroll. */
        header.fixed {
          background-color: var(--vibe-color-primary) !important;
        }
        /* Inner header-bar — the visible nav surface. */
        .header-bar {
          background-color: var(--vibe-color-primary) !important;
        }

        /* Force all nav / button / heading text to the on-primary color
           in every state (transparent, scrolled, over-solid-hero). */
        header.fixed .header-bar a.font-body,
        header.fixed .header-bar nav a,
        header.fixed .header-bar button.font-body,
        header.fixed .header-bar .font-heading {
          color: var(--vibe-color-text-on-primary) !important;
        }
        header.fixed .header-bar nav a:hover,
        header.fixed .header-bar button.font-body:hover {
          color: var(--vibe-color-text-on-primary) !important;
        }

        /* The "More" / parent-page dropdown panel paints on the same
           primary-colored surface as the header bar in this theme, so its
           links use the on-primary token to stay readable. The base rise
           theme renders the dropdown <div> with an inline
           \`backgroundColor: var(--vibe-color-background)\` (white) and
           paints links in \`--vibe-color-text\` — both fight us here, so we
           override the panel background to primary (with !important to
           beat the inline style), soften the border to a translucent
           on-primary tint to match the rest of the bar, and force the
           link text to on-primary in every state. The child-page indented
           rows inside the "More" overflow use \`--vibe-color-text-muted\`
           in the base; we tint that to a slightly faded on-primary for
           the same dim-but-readable effect. */
        header.fixed .header-bar .nav-more-dropdown {
          background-color: var(--vibe-color-primary) !important;
          border-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 20%, transparent) !important;
        }
        header.fixed .header-bar .nav-more-dropdown a,
        header.fixed .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text-on-primary) !important;
        }
        /* Indented child rows in the "More" overflow (rise paints these
           in text-muted) — dim slightly so the visual hierarchy survives
           on the primary surface. */
        header.fixed .header-bar .nav-more-dropdown a[class*="text-muted"],
        header.fixed .header-bar .nav-more-dropdown a[class*="text-[var(--vibe-color-text-muted)]"] {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 75%, transparent) !important;
        }

        /* Mobile drawer (hamburger menu) — paints on the same primary
           surface as the header bar so the open drawer feels like a
           continuation of the bar rather than a separate white sheet.
           The base rise theme sets the panel background inline to
           \`--vibe-color-background\` (white) and paints every link in
           \`--vibe-color-text\` (dark), which on top of the Via6 primary
           leaves the drawer reading white-on-white once we flip the
           surface — so we override the bg to primary (with !important
           to beat the inline style), force every link / button to the
           on-primary color, soften the inter-row dividers and the
           address/phone section border, and re-skin the outline pills
           (View Floor Plans / Apply Now / Resident Access) to read
           against the new dark surface (on-primary border + label,
           on-primary fill on hover with the primary color as the label). */
        header.fixed .mobile-menu-panel {
          background-color: var(--vibe-color-primary) !important;
        }
        header.fixed .mobile-menu-panel a,
        header.fixed .mobile-menu-panel button,
        header.fixed .mobile-menu-panel a:hover {
          color: var(--vibe-color-text-on-primary) !important;
        }
        /* Per-row bottom borders the rise theme paints in
           --vibe-color-border on the white drawer — soften to a
           translucent on-primary tint so the row separators still read. */
        header.fixed .mobile-menu-panel a[class*="border-b"] {
          border-bottom-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 20%, transparent) !important;
        }
        /* The address/phone block at the bottom is separated by a top
           border in the same border token — same softening treatment. */
        header.fixed .mobile-menu-panel div[class*="border-t"] {
          border-top-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 20%, transparent) !important;
        }
        /* Drawer CTA pills: rise paints border + label in
           --vibe-color-primary on white, which collapses on our primary
           surface. Flip to on-primary border + label, and on hover fill
           with on-primary so the label flips to primary. The hover
           background utility (\`hover:bg-[var(--vibe-color-primary)]\`)
           also resolves to our new bar color and would leave the pill
           invisible on hover — override the hover fill explicitly. */
        header.fixed .mobile-menu-panel .mobile-util-pill {
          border-color: var(--vibe-color-text-on-primary) !important;
          color: var(--vibe-color-text-on-primary) !important;
        }
        header.fixed .mobile-menu-panel .mobile-util-pill:hover {
          background-color: var(--vibe-color-text-on-primary) !important;
          color: var(--vibe-color-primary) !important;
        }
        /* Map-pin / phone icons inside the address+phone rows are
           painted in --vibe-color-primary by the rise theme — flip to
           on-primary so they stay visible on the new dark surface. */
        header.fixed .mobile-menu-panel svg[class*="text-[var(--vibe-color-primary)]"] {
          color: var(--vibe-color-text-on-primary) !important;
        }

        /* ============================================================
           Nest child pages under their primary parent in the mobile
           drawer (Via6 brand).

           The base rise theme renders parents and their children as a
           flat list of MobileNavLink siblings with identical styling
           (\`block py-3.5 px-6 ... font-semibold uppercase\`) — children
           appear visually indistinguishable from top-level pages, so the
           drawer reads as a flat 8-row list rather than as the same
           parent → children grouping the desktop dropdown surfaces.

           The markMobileNavChildren injection above tags child anchors
           with the data-vibe-nav-child attribute (label-matched against
           merged pages.json + the injected Schedule-A-Tour link). The
           rules below paint those rows as visually subordinate:

             - Indent the label with extra left padding (3.25rem vs base
               6 = 1.5rem) so the child label clearly hangs off the parent.
             - Drop the font size and weight a half-step so children read
               as supporting rows rather than equals.

           (Earlier revisions also painted a 2px secondary-color accent
           bar on the left edge via box-shadow inset, but the brand owner
           wanted that line removed — indentation + type-weight contrast
           alone carry the hierarchy.)

           Targeted at \`header.fixed .mobile-menu-panel nav a[...]\`
           with !important so this beats the Tailwind utility classes the
           base anchors carry (\`px-6 py-3.5 text-sm font-semibold\`).
           ============================================================ */
        header.fixed .mobile-menu-panel nav a[data-vibe-nav-child] {
          padding-left: 3.25rem !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          opacity: 0.85;
        }

        /* Utility row (phone, address, Qualifying Criteria, Apply Now,
           Resident Access) — repainted to --vibe-color-secondary so the
           upper bar reads as a distinct accent band above the primary main
           nav row. The base rise theme doesn't paint a background on the
           .header-util-row itself (it inherits from the .header-bar parent),
           so the override here is what introduces the band — the
           background-color rule below wins because it's !important and
           there's no competing inline style.

           Text / icon / divider tokens stay pinned to --vibe-color-text-
           on-primary even though the row surface is secondary. The Via6
           palette intentionally pairs the on-primary foreground with both
           brand colors (primary + secondary share the same target contrast
           color), so the upper band reads with the same label color as the
           main nav row below — keeps the whole header feeling visually
           unified even though the surfaces differ. */
        header.fixed .header-util-row {
          background-color: var(--vibe-color-secondary) !important;
          border-bottom-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 18%, transparent) !important;
        }
        header.fixed .header-util-row .header-util-link {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 85%, transparent) !important;
        }
        header.fixed .header-util-row .header-util-link:hover {
          color: var(--vibe-color-text-on-primary) !important;
        }
        header.fixed .header-util-row .header-util-divider {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 30%, transparent) !important;
        }
        /* Phone / map-pin icons in the row — pin to on-primary so the icon
           glyphs match the link labels even though the row surface is
           painted secondary. */
        header.fixed .header-util-row svg {
          color: var(--vibe-color-text-on-primary) !important;
        }

        /* ============================================================
           Hide the Qualifying Criteria link (The Majestic override).

           The Weidner parent _HeaderBase renders a Qualifying Criteria
           link in two places:

             1. Desktop utility row, between the address block and the
                Apply Now / Resident Access links.
             2. Mobile drawer, as a full-width pill above the
                [Apply Now | Resident Access] grid.

           The brand owner asked to drop this link on The Majestic — the
           base PDF treatment isn't required for this property. Since
           the parent owns the JSX (this file only wraps <BaseHeader />),
           we can't omit the anchor at render time — we hide it with CSS.

           Identification:
             - Desktop: the address anchor and the Qualifying Criteria
               anchor BOTH render as \`a.header-util-link\` with
               rel="noopener noreferrer" (Google Maps + the PDF both
               open in a new tab), so rel alone is not enough — an
               earlier version of this rule hid the address by mistake.
               The address anchor carries \`aria-label="View on Google
               Maps"\`, while Qualifying Criteria has no aria-label, so
               \`[rel="noopener noreferrer"]:not([aria-label])\` cleanly
               isolates Qualifying Criteria. (Apply Now / Resident
               Access render with target="_self" and no rel, so they
               were already excluded.)
             - Mobile drawer: only the Qualifying Criteria pill uses
               the \`.mobile-util-pill\` class with rel="noopener
               noreferrer"; the mobile address anchor renders without
               that class (it lives in the address/phone block at the
               bottom of the drawer with its own utility classes), so
               the mobile selector doesn't need the aria-label guard.

           Both anchors render inside an EditableSlotGroup wrapper <div>.
           We use \`:has(> a[...])\` to walk from the anchor up to its
           wrapper and display: none the wrapper so the flex row /
           mobile pill stack collapses without leaving an empty slot.
           The desktop utility row also emits a trailing
           \`.header-util-divider\` span after the wrapper — we hide
           that too so the remaining row doesn't lead with an orphaned
           pipe before Apply Now.

           :has() support landed in all major engines by 2022–2023
           (Chrome 105, Safari 15.4, Firefox 121), well inside the
           support window for this property's audience.
           ============================================================ */
        header.fixed .header-util-row :has(> a.header-util-link[rel="noopener noreferrer"]:not([aria-label])),
        header.fixed .header-util-row :has(> a.header-util-link[rel="noopener noreferrer"]:not([aria-label])) + .header-util-divider,
        header.fixed .mobile-menu-panel :has(> a.mobile-util-pill[rel="noopener noreferrer"]) {
          display: none !important;
        }

        /* Hamburger bars flip to on-primary too. */
        header.fixed span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--vibe-color-text-on-primary) !important;
        }

        /* ============================================================
           Hover underline → secondary brand color

           The base rise theme paints every nav-link hover underline with
           \`text-decoration-color: currentColor\` — so the underline
           inherits the link's own color (white on the primary bar / dark
           on a white surface). Via6's brand surfaces the secondary token
           as the hover affordance instead, so it stays consistent
           regardless of which surface the link sits on (main bar,
           dropdown panel, mobile drawer, utility row).

           Two selectors cover every underline-on-hover surface in this
           header:
             - \`.rise-nav-darken-hover\` — desktop nav, "More" dropdown,
               parent-page dropdown, and mobile drawer rows.
             - \`.header-util-link\` — the utility row (phone, address,
               Apply Now, Resident Access), which paints its own
               underline rather than going through .rise-nav-darken-hover.

           Both rules override only the *color* of the underline; the
           thickness / offset / transition curves come from the base. */
        .rise-nav-darken-hover:hover,
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: var(--vibe-color-secondary) !important;
        }
        /* Utility-row link hover underline: row paints in
           --vibe-color-secondary but text + underline are pinned to
           on-primary to stay visually unified with the main nav row. */
        header.fixed .header-util-row .header-util-link:hover {
          text-decoration-color: var(--vibe-color-text-on-primary) !important;
        }

        /* ============================================================
           Dropdown nav page hover → full secondary-color fill

           Inside the "More" overflow dropdown and the parent-page
           child-pages dropdown, we trade the inherited underline
           affordance for a full-row background fill in
           --vibe-color-secondary (with text flipping to
           --vibe-color-text-on-secondary). This makes each menu item
           feel like a discrete pressable cell — the standard pattern
           for stacked dropdown menus — while still keeping the
           underline treatment in place for the inline desktop nav row
           and mobile drawer above.

           Implementation:
             1. Suppress the .rise-nav-darken-hover underline ONLY when
                the link is inside .nav-more-dropdown (text-decoration
                back to none + transparent so the base's underline-color
                animation doesn't reappear).
             2. Paint the row background in --vibe-color-secondary on
                hover/focus and flip the text color to on-secondary.
                Cover the indented child rows (which the rise theme
                paints in text-muted) with the same hover so the muted
                tint doesn't fight the on-secondary text once hovered.
           ============================================================ */
        header.fixed .header-bar .nav-more-dropdown a.rise-nav-darken-hover,
        header.fixed .header-bar .nav-more-dropdown a.rise-nav-darken-hover:hover {
          text-decoration-line: none !important;
          text-decoration-color: transparent !important;
        }
        header.fixed .header-bar .nav-more-dropdown a:hover,
        header.fixed .header-bar .nav-more-dropdown a:focus-visible {
          background-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-primary) !important;
        }
        /* Indented child rows inside "More" — same full-fill hover so
           the dimmed muted tint doesn't bleed through the secondary
           background. Higher specificity than the muted-tint rule above. */
        header.fixed .header-bar .nav-more-dropdown a[class*="text-muted"]:hover,
        header.fixed .header-bar .nav-more-dropdown a[class*="text-[var(--vibe-color-text-muted)]"]:hover {
          background-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-primary) !important;
        }

        /* ============================================================
           Thicker nav bar + centered nav links (Via6 brand)

           The rise theme paints the main nav row at \`h-16\` (64px) on
           mobile / \`lg:h-[72px]\` (72px) on desktop, and pins the
           desktop nav with \`justify-end ... flex-1\` so links cluster
           against the Schedule-a-Tour CTA on the right. Via6's brand
           wants a more substantial bar with the page links visually
           centered between the logo and the CTA — so we:

             1. Raise the row height to 80px mobile / 104px desktop
                (targeted via the stable \`.h-16\` class on the rise
                row container; the desktop bump is gated by the lg
                breakpoint to match the original responsive setup).
             2. Flip the desktop nav's flex justification from
                \`justify-end\` to \`justify-center\` so the page links
                spread to the middle of their flex-1 container rather
                than hugging the CTA.

           Targeted with the \`aria-label="Main navigation"\` attribute
           (a stable contract of the rise DesktopNav <nav>) so a future
           class refactor in the base theme can't silently break this
           override.
           ============================================================ */
        .header-bar .flex.items-center.h-16 {
          height: 80px !important;
        }
        @media (min-width: 1024px) {
          .header-bar .flex.items-center.h-16 {
            height: 104px !important;
          }
        }
        .header-bar nav[aria-label="Main navigation"] {
          justify-content: center !important;
        }

        /* ============================================================
           Center dropdown nav pages (Via6 brand)

           The rise theme paints links inside the "More" overflow and the
           parent-page child dropdown (.nav-more-dropdown) as left-aligned
           rows — fine on a white surface where the leading edge of the
           label aligns under the parent trigger, but on the Via6 primary
           surface the brand reads better with the labels centered in
           each stacked cell (matches the centered main-nav row above).

           Two layers of centering:
             1. text-align: center on every <a> in the dropdown — covers
                indented child rows whose text wraps inside a single anchor.
             2. justify-content: center if the rise theme renders the
                anchor as a flex row (icon + label) — keeps the icon+label
                pair centered together as a unit rather than just centering
                the text within a left-anchored flex item.
           ============================================================ */
        header.fixed .header-bar .nav-more-dropdown a {
          text-align: center !important;
          justify-content: center !important;
        }

        /* Schedule-a-Tour CTA: the base paints border + label in the
           luminance-picked --rise-nav-cta-accent (resolves to
           --vibe-color-primary when primary has contrast vs white). On
           our new primary-color bar that's primary-on-primary, so we
           override both vars to the on-primary token. Hover fills with
           on-primary and flips the label back to primary. */
        header.fixed .nav-cta-btn {
          border-color: var(--vibe-color-text-on-primary) !important;
          color: var(--vibe-color-text-on-primary) !important;
        }
        header.fixed .nav-cta-btn::before {
          background: var(--vibe-color-text-on-primary) !important;
        }
        header.fixed .nav-cta-btn:hover {
          color: var(--vibe-color-primary) !important;
        }

        /* ============================================================
           Hide the Schedule-a-Tour pill on mobile (The Majestic override).

           The base rise Header renders the schedule_tour_link CTA
           (\`.nav-cta-btn\`) in the main nav row's right group with no
           responsive hide — so on mobile it sits next to the hamburger
           at every breakpoint. The brand owner asked to drop it on
           mobile; the Schedule-a-Tour action still reaches phones via
           the drawer / interior pages. Since the parent owns the JSX
           (this file only wraps <BaseHeader />), we can't drop the
           anchor at render time, so we hide it with CSS below the base
           theme's \`lg\` (1024px) breakpoint — the same cutoff the base
           uses for its desktop/mobile split.

           We hide the EditableSlotGroup wrapper (via :has(> a.nav-cta-btn))
           rather than the anchor alone, so the \`gap-3\` flex row doesn't
           leave an empty gap before the hamburger. In edit mode the
           wrapper is the data-vibesite-slot-group <div>; in preview /
           production it's a plain <div> — the :has() walk matches both.
           ============================================================ */
        @media (max-width: 1023.98px) {
          header.fixed .header-bar :has(> a.nav-cta-btn) {
            display: none !important;
          }
        }

        /* The "More" dropdown panel and mobile drawer both sit on a
           white background, so their link colors stay theme-text — the
           base styles already handle this and we don't override them. */

        /* ============================================================
           Round all CTAs (Via6 brand)
           The rise theme paints CTAs as squared/2px-radius rectangles.
           Via6's brand language uses pill-shaped action buttons, so we
           force every CTA class in the registry to border-radius: 9999px.
           The Header injects these once and they cascade to every page,
           since Header renders on every route.

           Hover-fill ::before pseudos inherit the parent's clip because
           each CTA already wraps them in overflow:hidden — no separate
           radius needed on the pseudo itself.
           ============================================================ */
        .nav-cta-btn,
        .hero-cta,
        .hero-solid-cta-btn,
        .cta-link-btn,
        .pinnedcallouts-cta,
        .tabbed-cta,
        .pet-cta,
        .special-offers-cta,
        .frc-cta,
        .ra-btn,
        .ty-btn {
          border-radius: 9999px !important;
        }

        /* ============================================================
           Pre-titles & subtitles in the heading font (Via6 brand)
           Via6 Living uses the same elegant serif (Prata) for the
           eyebrow above the headline and the lead beneath it,
           not just the H1. The rise components render those secondary
           lines with the body-font Tailwind class (\`font-body\`), so we
           target their EditableToken wrappers — every <RichTextContent>
           emits a [data-vibesite-token$=".pre_title|.subtitle"] node —
           and force them to inherit --vibe-font-heading. The inner
           <span data-vibesite-richtext> is display:contents so the
           font cascades into the rendered HTML; we still override it
           explicitly in case a rich-text payload set its own family.
           ============================================================ */
        [data-vibesite-token$=".pre_title"],
        [data-vibesite-token$=".subtitle"],
        [data-vibesite-token$=".pre_title"] [data-vibesite-richtext],
        [data-vibesite-token$=".subtitle"] [data-vibesite-richtext] {
          font-family: var(--vibe-font-heading) !important;
        }

        /* ============================================================
           Remove all-caps from headings, pre-titles, subtitles, and
           nav page links (Via6 brand)

           The rise theme paints H1/H2 headings, the pre-title eyebrow,
           and the subtitle line with the Tailwind \`uppercase\` utility
           (e.g. Hero \`hero.title\`, PageHero, HeroSolidBG, and the
           \`tracking-[0.25em] uppercase\` pre-title in Hero/CTASection).
           Via6's typography uses sentence-case serif throughout — caps
           clash with Prata — so we force text-transform: none.

           Selector strategy (defense in depth — the data-attribute
           selectors only land in edit mode, where EditableToken adds the
           data-vibesite-token wrapper; in plain preview / production the
           wrapper is the raw <h1>/<p> with no data attribute, so we also
           need class-based selectors that target the rise theme's actual
           rendered Tailwind classes):

             1. Headings — every heading tag carrying the .font-heading
                utility (Hero h1, PageHero h1, HeroSolidBG h1, CTASection
                panel h2s). Scoping to h1–h6 keeps button-style CTAs that
                also use .font-heading (e.g. HeroSolidBG's CTA pill) from
                losing their caps.
             2. Hero pre_title / subtitle — the rise Hero pins these to
                .hero-entrance-1 (pre_title <p>) and .hero-entrance-3
                (subtitle <p>), so target those animation classes
                directly. These are stable contracts of the rise Hero.
             3. data-vibesite-token wrappers — covers edit-mode
                rendering for every other component (CTASection labels,
                section titles on other pages, etc.) where the wrapper
                element does carry the token path attribute.

           Nav links: rise paints desktop links, the "More" dropdown
           items, and the mobile drawer items with the
           \`.rise-nav-darken-hover\` class plus \`uppercase tracking-wider\`.
           Targeting that class strips caps from every nav surface in
           one rule (desktop bar + dropdown + drawer all share it).
           ============================================================ */
        h1.font-heading,
        h2.font-heading,
        h3.font-heading,
        h4.font-heading,
        h5.font-heading,
        h6.font-heading,
        .hero-entrance-1,
        .hero-entrance-3,
        [data-vibesite-token$=".title"],
        [data-vibesite-token$=".pre_title"],
        [data-vibesite-token$=".subtitle"],
        [data-vibesite-token$=".title"] [data-vibesite-richtext],
        [data-vibesite-token$=".pre_title"] [data-vibesite-richtext],
        [data-vibesite-token$=".subtitle"] [data-vibesite-richtext],
        .rise-nav-darken-hover {
          text-transform: none !important;
        }
      `}</style>
    </>
  );
}

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = baseSchema;

export default Header;
